import React from 'react'
import { useState } from 'react'
import { styled } from 'styled-components'
import classnames from 'classnames'
import type {
	MapIcon,
	StudentSpecificContextualDataEntry,
} from '@mission.io/mission-toolkit'
import { ExpandedDataView } from './ExpandedData'

type Props = {
	content: StudentSpecificContextualDataEntry,
	className?: string,
}

/**
 * A card that displays a multimedia content object.
 */
export function MultiMediaCard({
	content,
	className,
	...props
}: Props): React$Node {
	const canExpand = content.text.length > 0 || content.media.length > 0
	const [isExpanded, setIsExpanded] = useState(false)

	return (
		<>
			<button
				{...props}
				// add a custom label for the button here because this button is a card and has lots of content (title, subtitle, icon)
				aria-label={content.title}
				className={classnames(
					className,
					'shadow-game-primary-800 shadow-md cursor-pointer text-left transition-all bg-[#5c6992] w-full min-w-200px rounded p-4',
					'hover:scale-[1.025] hover:shadow-game-primary-900 hover:shadow-lg'
				)}
				onClick={event => {
					canExpand && setIsExpanded(true)
				}}>
				<div className="flex gap-4">
					{content.icon && (
						<IconGridItem>
							<IconWrapper>
								<SVGIconFillContainer
									className="size-8 m-auto"
									icon={{ url: content.icon, width: 32, height: 32 }}
								/>
							</IconWrapper>
						</IconGridItem>
					)}
					<div className="flex flex-1 justify-between">
						<div>
							<h4 className="flex-1">{content.title}</h4>
							{content.subtitle && (
								<p className="text-white pt-2 -mt-1">{content.subtitle}</p>
							)}
						</div>
					</div>
				</div>
			</button>
			{isExpanded && (
				<ExpandedDataView
					contextualData={content}
					onFinishClose={() => setIsExpanded(false)}
				/>
			)}
		</>
	)
}

const IconGridItem = styled.div`
	grid-area: icon;
`

const IconWrapper = styled.div`
	width: 25px;
	height: 25px;
	margin: auto;
`

/**
 * Gets an SVG icon component for the given `MapIcon`
 */
function SVGIcon({ icon, ...props }: { icon: MapIcon }) {
	return (
		<svg {...props}>
			<image
				xlinkHref={icon.url}
				height={icon.height || 20}
				width={icon.width || 20}
			/>
		</svg>
	)
}

const SVGIconFillContainer = styled(SVGIcon)`
	width: 100%;
	height: 100%;
`
